import { useCallback } from "react";

/** 统一剥离 .final，保证流式 chunk/done 与最终 action_answer 命中同一 fileId。 */
function resolveActionFileId(name, filename) {
  const rawFilename = filename || name || "";
  const isFinalFile = rawFilename.includes(".final");
  const baseFilename = isFinalFile ? rawFilename.replace(".final", "") : rawFilename;
  return {
    fileId: name ? `${name}_${baseFilename}` : baseFilename,
    baseFilename,
    isFinalFile,
    rawFilename,
  };
}

const useWsActionAnswerHandler = ({
  setFiles,
  getLanguage,
  setConflictDialog,
  setActiveFile,
  isRestoringArchiveRef,
  isRestoringArchive,
  addLocalMessage,
  setProjectState,
}) => {
  return useCallback((data) => {
    // 处理流式文档增量推送
    if (data.type === "streaming_action_chunk") {
      if (!data.name) return true;
      const { fileId, baseFilename, rawFilename } = resolveActionFileId(data.name, data.filename || data.name);
      const chunk = data.result || "";

      setFiles((prevFiles) => {
        const existingFile = prevFiles[fileId];
        if (existingFile) {
          return {
            ...prevFiles,
            [fileId]: {
              ...existingFile,
              content: existingFile.content + chunk,
              meta: {
                ...existingFile.meta,
                lastUpdated: new Date().getTime(),
                isStreaming: true,
              },
            },
          };
        }
        return {
          ...prevFiles,
          [fileId]: {
            content: chunk,
            originalContent: "",
            rawContent: "",
            language: getLanguage(baseFilename),
            meta: {
              role: data.name,
              filename: baseFilename,
              originalFilename: rawFilename,
              timestamp: new Date().toISOString(),
              version: 1,
              isStreaming: true,
            },
          },
        };
      });

      if (!isRestoringArchiveRef.current) {
        setActiveFile(fileId);
      }
      return true;
    }

    // 处理流式文档完成标记：将 originalContent 同步为当前累积内容
    // 这样后续 action_answer（isFinalFile）到达时 originalContent 不为空
    if (data.type === "streaming_action_done") {
      if (!data.name) return true;
      const { fileId } = resolveActionFileId(data.name, data.filename || data.name);

      setFiles((prevFiles) => {
        const existingFile = prevFiles[fileId];
        if (existingFile) {
          return {
            ...prevFiles,
            [fileId]: {
              ...existingFile,
              originalContent: existingFile.content,
              rawContent: existingFile.content,
              meta: {
                ...existingFile.meta,
                isStreaming: false,
              },
            },
          };
        }
        return prevFiles;
      });
      return true;
    }

    if (data.type !== "action_answer") return false;

    if (!data.filename) {
      console.warn("Received action_answer without filename:", data);
      return true;
    }

    const { fileId, baseFilename, isFinalFile, rawFilename } = resolveActionFileId(data.name, data.filename);
    const isDraftFile = rawFilename.endsWith(".draft");
    const newContentC = data.result;

    console.log("📥 接收 action_answer:", {
      filename: data.filename,
      name: data.name,
      fileId: fileId,
      contentPreview: newContentC?.substring(0, 100),
      timestamp: new Date().toISOString(),
      isRestoringArchive: isRestoringArchive,
    });

    let shouldShowConflictDialog = false;
    let conflictData = null;

    setFiles((prevFiles) => {
      const existingFile = prevFiles[fileId];

      if (isFinalFile && existingFile) {
        const originalA = existingFile.originalContent;
        return {
          ...prevFiles,
          [fileId]: {
            content: newContentC,
            originalContent: originalA,
            rawContent: originalA,
            language: getLanguage(baseFilename),
            meta: {
              role: data.name,
              filename: baseFilename,
              originalFilename: data.filename,
              timestamp: data.timestamp || new Date().toISOString(),
              version: (existingFile.meta?.version || 0) + 1,
              isDraft: isDraftFile,
              isFinal: true,
              phase: data.phase || "",
              roleContext: data.role || "",
              taskFilename: data.task_filename || "",
            },
          },
        };
      }

      if (existingFile) {
        const currentB = existingFile.content;
        const originalA = existingFile.originalContent;
        const hasRevisions = currentB !== originalA;

        if (hasRevisions) {
          shouldShowConflictDialog = true;
          conflictData = {
            fileId,
            newContent: newContentC,
            currentContent: currentB,
            originalContent: originalA,
            filename: data.filename,
            meta: {
              role: data.name,
              filename: data.filename,
              timestamp: data.timestamp || new Date().toISOString(),
              isDraft: isDraftFile,
              phase: data.phase || "",
              roleContext: data.role || "",
              taskFilename: data.task_filename || "",
            },
          };
          return prevFiles;
        }
      }

      return {
        ...prevFiles,
        [fileId]: {
          content: newContentC,
          originalContent: newContentC,
          rawContent: newContentC,
          language: getLanguage(baseFilename),
          meta: {
            role: data.name,
            filename: baseFilename,
            originalFilename: data.filename,
            timestamp: data.timestamp || new Date().toISOString(),
            version: existingFile ? (existingFile.meta?.version || 0) + 1 : 1,
            isDraft: isDraftFile,
            isFinal: isFinalFile,
            phase: data.phase || "",
            roleContext: data.role || "",
            taskFilename: data.task_filename || "",
          },
        },
      };
    });

    if (shouldShowConflictDialog) {
      setConflictDialog({
        isOpen: true,
        ...conflictData,
      });
      return true;
    }

    if (!isRestoringArchiveRef.current) {
      requestAnimationFrame(() => {
        setActiveFile(fileId);
      });
    }

    if (isFinalFile && addLocalMessage) {
      addLocalMessage(
        `📋 **收到定稿文档：${baseFilename}**\n\n` +
          `✅ 已自动采用定稿版本\n` +
          `📊 显示定稿内容相对于原始版本的差异\n` +
          `📋 文档长度：${newContentC.length} 字符\n` +
          `🎯 文档状态：已定稿`
      );
    }

    switch (data.name) {
      case "WriteCode":
      case "WriteCodeReview":
        setProjectState("coding_review");
        break;
      case "WriteTest":
      case "RunCode":
        setProjectState("testing_execution");
        break;
      default:
        break;
    }

    return false;
  }, [
    addLocalMessage,
    getLanguage,
    isRestoringArchive,
    isRestoringArchiveRef,
    setActiveFile,
    setConflictDialog,
    setFiles,
    setProjectState,
  ]);
};

export default useWsActionAnswerHandler;
